import { createSlice } from "@reduxjs/toolkit";
import {
  createBusinessPartnerRequest,
  createChartAccountRequest,
  createJournalEntryRequest,
  getBusinessPartnerRequest,
  getBusinessPartnersRequest,
  getBaseChartAccountTemplates,
  getChartAccountRequest,
  getChartAccountsRequest,
  getJournalEntriesRequest,
  getJournalEntryRequest,
  updateBusinessPartnerRequest,
  updateChartAccountRequest,
  updateJournalEntryRequest,
} from "../apis/accounting";

const initialState = {
  isLoading: false,
  error: null,
  templates: [],
  subTemplates: [],
  subSubTemplates: [],
  chartAccounts: [],
  chartAccountsPage: 1,
  chartAccountsLimit: 10,
  chartAccountsTotalPages: 1,
  chartAccountsTotalResults: 0,
  selectedChartAccount: null,
  businessPartners: [],
  businessPartnersPage: 1,
  businessPartnersLimit: 10,
  businessPartnersTotalPages: 1,
  businessPartnersTotalResults: 0,
  selectedBusinessPartner: null,
  journalEntries: [],
  journalEntriesPage: 1,
  journalEntriesLimit: 10,
  journalEntriesTotalPages: 1,
  journalEntriesTotalResults: 0,
  selectedJournalEntry: null,
};

const slice = createSlice({
  name: "accounting",
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET TEMPLATES
    getTemplatesSuccess(state, action) {
      state.isLoading = false;
      state.templates = action.payload;
    },

    // GET SUB TEMPLATES
    getSubTemplatesSuccess(state, action) {
      state.isLoading = false;
      state.subTemplates = action.payload;
    },

    // GET SUB SUB TEMPLATES
    getSubSubTemplatesSuccess(state, action) {
      state.isLoading = false;
      state.subSubTemplates = action.payload;
    },

    // GET CHART ACCOUNTS
    getChartAccountsSuccess(state, action) {
      state.isLoading = false;
      state.chartAccounts = action.payload.results;
      state.chartAccountsPage = action.payload.page;
      state.chartAccountsLimit = action.payload.limit;
      state.chartAccountsTotalPages = action.payload.totalPages;
      state.chartAccountsTotalResults = action.payload.totalResults;
    },

    // GET CHART ACCOUNT
    getChartAccountSuccess(state, action) {
      state.isLoading = false;
      state.selectedChartAccount = action.payload;
    },

    // GET BUSINESS PARTNERS
    getBusinessPartnersSuccess(state, action) {
      state.isLoading = false;
      state.businessPartners = action.payload.results;
      state.businessPartnersPage = action.payload.page;
      state.businessPartnersLimit = action.payload.limit;
      state.businessPartnersTotalPages = action.payload.totalPages;
      state.businessPartnersTotalResults = action.payload.totalResults;
    },

    // GET BUSINESS PARTNER
    getBusinessPartnerSuccess(state, action) {
      state.isLoading = false;
      state.selectedBusinessPartner = action.payload;
    },

    getJournalEntriesSuccess(state, action) {
      state.isLoading = false;
      state.journalEntries = action.payload.results;
      state.journalEntriesPage = action.payload.page;
      state.journalEntriesLimit = action.payload.limit;
      state.journalEntriesTotalPages = action.payload.totalPages;
      state.journalEntriesTotalResults = action.payload.totalResults;
    },

    getJournalEntrySuccess(state, action) {
      state.isLoading = false;
      state.selectedJournalEntry = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  startLoading,
  hasError,
  getTemplatesSuccess,
  getSubTemplatesSuccess,
  getSubSubTemplatesSuccess,
  getChartAccountsSuccess,
  getChartAccountSuccess,
  getBusinessPartnersSuccess,
  getBusinessPartnerSuccess,
  getJournalEntriesSuccess,
  getJournalEntrySuccess,
} = slice.actions;

// ----------------------------------------------------------------------

export function getTemplates() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getBaseChartAccountTemplates({ level: 1 });
      dispatch(slice.actions.getTemplatesSuccess(response.data.results));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSubTemplates(parentTemplateId) {
  return async (dispatch) => {
    if (!parentTemplateId) {
      dispatch(slice.actions.getSubTemplatesSuccess([]));
      return;
    }
    dispatch(slice.actions.startLoading());
    try {
      const response = await getBaseChartAccountTemplates({ parentTemplateId });
      dispatch(slice.actions.getSubTemplatesSuccess(response.data.results));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSubSubTemplates(parentTemplateId) {
  return async (dispatch) => {
    if (!parentTemplateId) {
      dispatch(slice.actions.getSubSubTemplatesSuccess([]));
      return;
    }
    dispatch(slice.actions.startLoading());
    try {
      const response = await getBaseChartAccountTemplates({ parentTemplateId });
      dispatch(slice.actions.getSubSubTemplatesSuccess(response.data.results));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createChartAccount(payload) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await createChartAccountRequest(payload);
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateChartAccount(id, payload) {
  return async (dispatch) => {
    if (!id) return null;

    dispatch(slice.actions.startLoading());
    try {
      const response = await updateChartAccountRequest(id, payload);
      const chartAccount = response.data?.result || response.data;
      dispatch(slice.actions.getChartAccountSuccess(chartAccount));
      return chartAccount;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function getChartAccounts({
  franchiseId,
  page = 1,
  limit = 10,
  search = "",
  code = "",
  status = "all",
  level = "3,4,5",
}) {
  return async (dispatch) => {
    if (!franchiseId) {
      dispatch(
        slice.actions.getChartAccountsSuccess({
          results: [],
          page: 1,
          limit,
          totalPages: 1,
          totalResults: 0,
        })
      );
      return;
    }

    dispatch(slice.actions.startLoading());
    try {
      const params = {
        franchiseId,
        page,
        limit,
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      if (code.trim()) {
        params.code = code.trim();
      }

      if (status !== "all") {
        params.status = status;
      }

      if (level !== "all") {
        params.level = level;
      }

      const response = await getChartAccountsRequest(params);
      dispatch(
        slice.actions.getChartAccountsSuccess({
          results: response.data.results || [],
          page: response.data.page || page,
          limit: response.data.limit || limit,
          totalPages: response.data.totalPages || 1,
          totalResults: response.data.totalResults || 0,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getChartAccount(id) {
  return async (dispatch) => {
    if (!id) return null;

    dispatch(slice.actions.startLoading());
    try {
      const response = await getChartAccountRequest(id);
      const chartAccount = response.data?.result || response.data;
      dispatch(slice.actions.getChartAccountSuccess(chartAccount));
      return chartAccount;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function createBusinessPartner(payload) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await createBusinessPartnerRequest(payload);
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateBusinessPartner(id, payload) {
  return async (dispatch) => {
    if (!id) return null;

    dispatch(slice.actions.startLoading());
    try {
      const response = await updateBusinessPartnerRequest(id, payload);
      const businessPartner = response.data?.result || response.data;
      dispatch(slice.actions.getBusinessPartnerSuccess(businessPartner));
      return businessPartner;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function getBusinessPartners({
  franchiseId,
  page = 1,
  limit = 10,
  search = "",
  status = "all",
  partnerRole = "all",
}) {
  return async (dispatch) => {
    if (!franchiseId) {
      dispatch(
        slice.actions.getBusinessPartnersSuccess({
          results: [],
          page: 1,
          limit,
          totalPages: 1,
          totalResults: 0,
        })
      );
      return;
    }

    dispatch(slice.actions.startLoading());
    try {
      const params = {
        franchiseId,
        page,
        limit,
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      if (status !== "all") {
        params.status = status;
      }

      if (partnerRole !== "all") {
        params.partnerRole = partnerRole;
      }

      const response = await getBusinessPartnersRequest(params);
      dispatch(
        slice.actions.getBusinessPartnersSuccess({
          results: response.data.results || [],
          page: response.data.page || page,
          limit: response.data.limit || limit,
          totalPages: response.data.totalPages || 1,
          totalResults: response.data.totalResults || 0,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getBusinessPartner(id) {
  return async (dispatch) => {
    if (!id) return null;

    dispatch(slice.actions.startLoading());
    try {
      const response = await getBusinessPartnerRequest(id);
      const businessPartner = response.data?.result || response.data;
      dispatch(slice.actions.getBusinessPartnerSuccess(businessPartner));
      return businessPartner;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

function normalizeJournalEntryResponse(data) {
  return data?.data?.result || data?.data || data?.result || data;
}

export function createJournalEntry(payload) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await createJournalEntryRequest(payload);
      const journalEntry = normalizeJournalEntryResponse(response.data);
      dispatch(slice.actions.getJournalEntrySuccess(journalEntry));
      return journalEntry;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateJournalEntry(id, payload) {
  return async (dispatch) => {
    if (!id) return null;

    dispatch(slice.actions.startLoading());
    try {
      const response = await updateJournalEntryRequest(id, payload);
      const journalEntry = normalizeJournalEntryResponse(response.data);
      dispatch(slice.actions.getJournalEntrySuccess(journalEntry));
      return journalEntry;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function getJournalEntries({
  franchiseId,
  page = 1,
  limit = 10,
  search = "",
  sourceType = "all",
}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const params = { page, limit };
      if (franchiseId) params.franchiseId = franchiseId;
      if (search.trim()) params.search = search.trim();
      if (sourceType !== "all") params.sourceType = sourceType;

      const response = await getJournalEntriesRequest(params);
      const payload = response.data?.data || response.data;
      const result = payload?.result;
      const results = payload?.results || result?.results || (Array.isArray(result) ? result : []);
      const pagination = result && !Array.isArray(result) ? result : payload;

      dispatch(
        slice.actions.getJournalEntriesSuccess({
          results,
          page: pagination?.page || page,
          limit: pagination?.limit || limit,
          totalPages: pagination?.totalPages || 1,
          totalResults: pagination?.totalResults ?? pagination?.total ?? results.length,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getJournalEntry(id) {
  return async (dispatch) => {
    if (!id) return null;

    dispatch(slice.actions.startLoading());
    try {
      const response = await getJournalEntryRequest(id);
      const journalEntry = normalizeJournalEntryResponse(response.data);
      dispatch(slice.actions.getJournalEntrySuccess(journalEntry));
      return journalEntry;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}
